import api from '../clients/api';
import { handleUnauthorized } from '../utils/apiWrapper';
import { notifyBugsnagNetwork } from '../utils/bugsnagClient';
import { 
  AddLernsetRequest, 
  AddPagesToLernsetRequest, 
  AddRichTextPartRequest, 
  UpdateLernsetRequest 
} from '@klab-berlin/api-sdk/lib/types/requests/Lernsets';
import services from '.';
import { Instance } from 'pspdfkit';
import { Lernset, PublicLernset } from '@klab-berlin/api-sdk/lib/types/responses/Lernsets';
import { AmplitudeTrackPayload } from '@klab-berlin/api-sdk/lib/types/requests/Track';
import { PDFDocument } from 'pdf-lib';
import htmlToPdfmake from 'html-to-pdfmake';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { TDocumentDefinitions } from 'pdfmake/interfaces';

const service = {
  addLernset,
  addPagesToLernset,
  createPublicLink,
  getLernsets,
  getPublicLernset,
  getPartsImages,
  deleteLernset,
  duplicateLernset,
  updateLernset,
  getLernsetPartToken,
  getLernsetDocumentUrl,
  getLernsetPartsTokens,
  rotateLernsetPart,
  trackLernset,
  addRichTextPart,
  getLernsetById,
  uploadImage,
  getImageBase64,
};

function addLernset(userId: string, payload: AddLernsetRequest) {
  notifyBugsnagNetwork('lernsets', 'addLernset', { userId, ...payload });
  return handleUnauthorized(() => api.lernsets.addLernset(userId, payload));
}

function getLernsets(userId: string) {
  notifyBugsnagNetwork('lernsets', 'getLernsets', { userId });
  return handleUnauthorized(() => api.lernsets.getLernsetsByOwner(userId));
}

function getPublicLernset(accessToken: string) {
  notifyBugsnagNetwork('lernsets', 'getPublicLernset', { accessToken });
  return api.lernsets.getPublicLernset(accessToken);
}

function getLernsetPartToken(lernsetId: string, partId: string) {
  notifyBugsnagNetwork('lernsets', 'getLernsetPartToken', { lernsetId, partId });
  return handleUnauthorized(() => api.lernsets.getLernsetPartToken(lernsetId, partId));
}

function getLernsetPartsTokens(lernsetId: string, accessToken?: string) {
  notifyBugsnagNetwork('lernsets', 'getLernsetPartsTokens', { lernsetId });
  return handleUnauthorized(() => api.lernsets.getLernsetPartsTokens(lernsetId, accessToken));
}

function addPagesToLernset(userId: string, lernsetId: string, payload: AddPagesToLernsetRequest) {
  notifyBugsnagNetwork('lernsets', 'addPagesToLernset', { userId, lernsetId, ...payload });
  return handleUnauthorized(() => api.lernsets.addPagesToLernset(userId, lernsetId, payload));
}

function getPartsImages(lernsetId: string, partIndices: number[], imageMaxSideLength: number, accessToken?: string) {
  notifyBugsnagNetwork('lernsets', 'getPartsImages', { lernsetId, partIndices, accessToken });
  return handleUnauthorized(() => api.lernsets.getPartsImages(lernsetId, partIndices, imageMaxSideLength, accessToken));
}

function deleteLernset(lernsetId: string) {
  notifyBugsnagNetwork('lernsets', 'deleteLernset', { lernsetId });
  return handleUnauthorized(() => api.lernsets.deleteLernset(lernsetId));
}

function duplicateLernset(lernsetId: string, partId: string) {
  notifyBugsnagNetwork('lernsets', 'duplicateLernset', { lernsetId, partId });
  return handleUnauthorized(() => api.lernsets.duplicateLernsetPart(lernsetId, partId));
}

function updateLernset(lernsetId: string, payload: UpdateLernsetRequest) {
  notifyBugsnagNetwork('lernsets', 'updateLernset', { lernsetId, payload });
  return handleUnauthorized(() => api.lernsets.updateLernset(lernsetId, payload));
}

function getImageBase64(imageUrl: string) {
  notifyBugsnagNetwork('lernsets', 'getImageBase64', { imageUrl });
  return handleUnauthorized(() => api.lernsets.getImageBase64(imageUrl));
}

function cleanMarkTagStyles(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;

  const marks = div.querySelectorAll('mark');
  marks.forEach((mark) => {
    const style = mark.getAttribute('style');
    if (style && style.includes('color: inherit')) {
      mark.setAttribute('style', style.replace('color: inherit', ''));
    }
  });

  return div.innerHTML;
}

async function replaceImagesWithBase64(html: string): Promise<string> {
  // Max width for images in PDF (A4 page), accounting for margins and padding, in pixels.
  const MAX_IMAGE_WIDTH_IN_PDF = 681.44;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const images = Array.from(doc.querySelectorAll('img'));

  const imagePromises = images.map(async (img) => {
    const srcUrl = img.getAttribute('src');
    if (srcUrl) {
      try {
        const { imageBase64 } = await getImageBase64(srcUrl);
        img.setAttribute('src', imageBase64);
        img.style.maxWidth = `${MAX_IMAGE_WIDTH_IN_PDF}px`;
        img.style.maxHeight = 'auto';
      } catch (error) {
        throw new Error(`Error fetching image: ${srcUrl}`);
      }
    }
  });

  await Promise.all(imagePromises);

  return doc.body.innerHTML;
}

async function convertHtmlToPdfBlob(partId: string): Promise<Blob> {
  const container = document.getElementById(partId);
  if (!container) throw new Error(`Element with ID '${partId}' not found.`);
  let html = container.innerHTML;

  // Remove inherited color styles from mark tags to avoid color issues in PDF
  if (html.includes('<mark')) html = cleanMarkTagStyles(html);

  if (html.includes('<img')) html = await replaceImagesWithBase64(html);
  
  const pdfmakeContent = htmlToPdfmake(html);

  const documentDefinition: TDocumentDefinitions = { 
    content: pdfmakeContent,
  };

  return new Promise((resolve, reject) => {
    pdfMake.vfs = pdfFonts.vfs;
    pdfMake.createPdf(documentDefinition).getBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Error generating PDF'));
      }
    });
  });
}

async function mergePdfBlobsToBuffer(partBlobs: Blob[], lernsetTitle: string) {
  const mergedPdf = await PDFDocument.create();

  for (const blob of partBlobs) {
    const arrayBuffer = await blob.arrayBuffer();
    const pdf = await PDFDocument.load(arrayBuffer);
    const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
    copiedPages.forEach((page) => mergedPdf.addPage(page));
  }

  mergedPdf.setTitle(lernsetTitle);
  mergedPdf.setAuthor('');

  const pdfBytes = await mergedPdf.save();

  return pdfBytes;
}

async function getLernsetDocumentUrl(lernset: Lernset | PublicLernset, accessToken?: string) {
  notifyBugsnagNetwork('lernsets', 'getLernsetDocumentUrl', { lernset });

  // Get all part-tokens of the lernset
  let partTokens: string[] = [];
  const hasPdfDocuments = lernset.parts.some(part => part.type === 'pdf');
  if (hasPdfDocuments) partTokens = await services.lernsets.getLernsetPartsTokens(lernset.id, accessToken);

  // Get all part-blobs of the lernset
  const partBlobs = await Promise.all(lernset.parts.map((part, index) => {
    if (part.type === 'markup-text') return convertHtmlToPdfBlob(part.id);

    else if (part.type === 'pdf') {
      const config = {
        headless: true,
        documentId: part.id,
        authPayload: { jwt: partTokens[index] },
        instant: false,
      };

      return window.PSPDFKit.load(config).then((instance: Instance) => {
        return instance.exportPDF()
          .then((documentBuffer) => 
            new Blob([documentBuffer], { type: 'application/pdf' })
          ).finally(() => {
            window.PSPDFKit.unload(instance);
          });
      });
    } else {
      throw new Error(`Unsupported part type: ${part.type}`);
    }
  }));

  const documentBuffer = await mergePdfBlobsToBuffer(partBlobs, lernset.title);
  const documentBlob = new Blob([documentBuffer], { type: 'application/pdf' });
  const documentUrl = URL.createObjectURL(documentBlob);

  return documentUrl;
}

async function rotateLernsetPart(lernsetId: string, partId: string, partIndex: number) {
  const partsTokens = await services.lernsets.getLernsetPartsTokens(lernsetId);

  const config = {
    headless: true,
    documentId: partId,
    authPayload: { jwt: partsTokens[partIndex] },
    instant: false,
  };

  const instance: Instance = await window.PSPDFKit.load(config);
  await instance.applyOperations([
    {
      type: 'rotatePages',
      pageIndexes: [0],
      rotateBy: 90
    }
  ]);

  window.PSPDFKit.unload(instance);
}

function createPublicLink(lernsetId: string) {
  notifyBugsnagNetwork('lernsets', 'createPublicLink', { lernsetId });
  return handleUnauthorized(() => api.lernsets.createPublicLink(lernsetId));
}

function trackLernset(lernsetId: string, payload: AmplitudeTrackPayload, accessToken?: string) {
  notifyBugsnagNetwork('lernsets', 'trackLernset', { lernsetId, payload, accessToken });
  return handleUnauthorized(() => api.lernsets.trackLernset(lernsetId, payload, accessToken));
}

function addRichTextPart(lernsetId: string, payload: AddRichTextPartRequest) {
  notifyBugsnagNetwork('lernsets', 'addRichTextPart', { lernsetId, payload });
  return handleUnauthorized(() => api.lernsets.addRichtextPart(lernsetId, payload));
}

function getLernsetById(lernsetId: string) {
  notifyBugsnagNetwork('lernsets', 'getLernsetById', { lernsetId });
  return handleUnauthorized(() => api.lernsets.getLernsetById(lernsetId));
}

function uploadImage(lernsetId: string, image: FormData) {
  notifyBugsnagNetwork('lernsets', 'uploadImage', { lernsetId, image });
  return handleUnauthorized(() => api.lernsets.uploadImage(lernsetId, image));
}

export default service;
